import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  BadgeCheck, MapPin, IndianRupee, Clock, Phone, MessageCircle, Languages, Stethoscope,
  Calendar, ChevronLeft, ChevronRight, ShieldCheck,
} from "lucide-react";

function fmtTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
}

function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export default function DoctorProfilePage() {
  const { slug } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [date, setDate] = useState(() => new Date());
  const [slotsData, setSlotsData] = useState({ slots: [], all_slots: [], taken: [] });
  const [contact, setContact] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [booking, setBooking] = useState(false);
  const [bookedAppt, setBookedAppt] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/doctors/${slug}`)
      .then((r) => setDoctor(r.data.doctor))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!doctor) return;
    const iso = date.toISOString().slice(0, 10);
    api.get(`/doctors/${slug}/slots`, { params: { date_str: iso } }).then((r) => setSlotsData(r.data));
  }, [doctor, slug, date]);

  const specialtyLabel = doctor && doctor.specialty_slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const areaLabel = doctor && doctor.area_slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const wa = useMemo(() => {
    if (!doctor) return "";
    return (doctor.whatsapp || doctor.phone || "").replace(/[^\d]/g, "");
  }, [doctor]);
  const waMsg = encodeURIComponent(`Hi Dr. ${doctor?.name}, I found you on LocalDoctor.in and would like to book an appointment.`);

  const sendContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.phone) {
      toast.error("Please enter your name and phone.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/contact-requests", { doctor_id: doctor.id, ...contact });
      toast.success("Request sent. The clinic will reach out to you.");
      setContact({ name: "", phone: "", message: "" });
      if (data.whatsapp_link) window.open(data.whatsapp_link, "_blank", "noopener");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const bookSlot = async (slotIso) => {
    if (!user || user.role !== "patient") {
      toast.message("Please log in as a patient to book.", { description: "Takes less than 30 seconds — just your mobile number." });
      navigate(`/patient/login?next=/doctor/${slug}`);
      return;
    }
    setBooking(true);
    try {
      const { data } = await api.post("/appointments", { doctor_id: doctor.id, slot_datetime: slotIso });
      setBookedAppt(data.appointment);
      toast.success("Appointment booked. Your OTP is ready.");
      // refresh slots
      const iso = date.toISOString().slice(0, 10);
      const r = await api.get(`/doctors/${slug}/slots`, { params: { date_str: iso } });
      setSlotsData(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBooking(false);
    }
  };

  if (notFound) {
    return (
      <div className="mx-auto max-w-[720px] px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Doctor not found</h1>
        <p className="text-muted-foreground mt-2">The profile you're looking for may have been removed.</p>
        <Link to="/doctors/pune" className="btn-primary mt-6">Browse Pune doctors</Link>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 lg:px-6 py-10">
        <div className="h-64 rounded-xl bg-muted/50 animate-pulse" />
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Physician",
    name: doctor.name,
    medicalSpecialty: specialtyLabel,
    telephone: doctor.phone,
    image: doctor.photo_url,
    address: {
      "@type": "PostalAddress",
      streetAddress: doctor.address,
      addressLocality: areaLabel,
      addressRegion: "Maharashtra",
      addressCountry: "IN",
    },
  };

  return (
    <>
      <Seo
        title={`${doctor.name} — ${specialtyLabel} in ${areaLabel}, Pune`}
        description={`Book an appointment with ${doctor.name} (${specialtyLabel}) at ${doctor.clinic_name}, ${areaLabel}, Pune. ${doctor.experience_years}+ years experience.`}
        canonicalPath={`/doctor/${doctor.slug}`}
        jsonLd={jsonLd}
        image={doctor.photo_url}
      />

      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-[1280px] px-4 lg:px-6 py-8 lg:py-12">
          <nav className="text-xs text-muted-foreground mb-4">
            <Link to="/" className="hover:text-foreground">Home</Link> <span className="mx-1">/</span>
            <Link to={`/doctors/pune`} className="hover:text-foreground">Pune</Link> <span className="mx-1">/</span>
            <Link to={`/doctors/pune/${doctor.specialty_slug}`} className="hover:text-foreground">{specialtyLabel}</Link> <span className="mx-1">/</span>
            <span>{doctor.name}</span>
          </nav>

          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 flex gap-5 items-start">
              <div className="h-24 w-24 lg:h-32 lg:w-32 rounded-xl overflow-hidden bg-muted ring-1 ring-border shrink-0">
                {doctor.photo_url ? (
                  <img src={doctor.photo_url} alt={doctor.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full grid place-items-center text-2xl font-bold text-muted-foreground">
                    {doctor.name.split(" ").slice(0, 2).map((p) => p[0]).join("")}
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {doctor.verified && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--ld-jade-fg))] bg-accent px-2 py-0.5 rounded-full">
                      <BadgeCheck size={13} /> Verified
                    </span>
                  )}
                  {doctor.is_demo && (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Demo Profile</span>
                  )}
                </div>
                <h1 className="font-display text-2xl md:text-3xl font-bold mt-2" data-testid="doctor-name">{doctor.name}</h1>
                <p className="text-muted-foreground mt-1">{specialtyLabel} · {doctor.qualification}</p>
                <p className="text-sm text-muted-foreground mt-1 tabular">{doctor.experience_years}+ years experience</p>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-foreground/80">
                  <span className="inline-flex items-center gap-1.5"><MapPin size={15} className="text-muted-foreground" />{doctor.clinic_name}, {areaLabel}</span>
                  <span className="inline-flex items-center gap-1.5 tabular"><IndianRupee size={15} className="text-muted-foreground" />{doctor.consultation_fee} consultation</span>
                  <span className="inline-flex items-center gap-1.5"><Clock size={15} className="text-muted-foreground" />{doctor.timings || "By appointment"}</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-4 flex flex-col gap-2 lg:items-end">
              <div className="flex gap-2 w-full lg:w-auto">
                <a href={`tel:${doctor.phone}`} className="btn-secondary flex-1" data-testid="profile-call-btn">
                  <Phone size={16} /> Call
                </a>
                {wa && (
                  <a href={`https://wa.me/${wa}?text=${waMsg}`} target="_blank" rel="noopener noreferrer" className="btn-secondary flex-1 text-[hsl(var(--ld-jade-fg))]" data-testid="profile-whatsapp-btn">
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                )}
              </div>
              <a href="#booking" className="btn-primary w-full lg:w-auto" data-testid="profile-book-btn">
                <Calendar size={16} /> Book an OTP appointment
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 lg:px-6 py-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-10">
          {doctor.about && (
            <div>
              <h2 className="font-display text-xl font-semibold">About {doctor.name}</h2>
              <p className="mt-3 text-foreground/80 leading-relaxed">{doctor.about}</p>
            </div>
          )}

          {doctor.services?.length > 0 && (
            <div>
              <h2 className="font-display text-xl font-semibold">Services</h2>
              <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                {doctor.services.map((s) => (
                  <li key={s} className="flex items-start gap-2 text-foreground/85 text-sm">
                    <Stethoscope size={16} className="mt-0.5 text-primary shrink-0" /> {s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {doctor.languages?.length > 0 && (
            <div>
              <h2 className="font-display text-xl font-semibold">Languages spoken</h2>
              <p className="mt-3 inline-flex items-center gap-2 text-foreground/85">
                <Languages size={16} className="text-primary" />
                {doctor.languages.join(" · ")}
              </p>
            </div>
          )}

          <div id="booking" className="rounded-2xl border border-border p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-xl font-semibold">Book a visit</h2>
                <p className="text-muted-foreground text-sm">Pick a slot — we'll generate an OTP you show at the clinic to skip the queue.</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setDate(addDays(date, -1))} className="btn-secondary px-3 py-2 text-sm" aria-label="Previous day" data-testid="booking-prev-day">
                  <ChevronLeft size={16} />
                </button>
                <span className="font-semibold text-sm px-2 min-w-[160px] text-center tabular">
                  {date.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" })}
                </span>
                <button onClick={() => setDate(addDays(date, 1))} className="btn-secondary px-3 py-2 text-sm" aria-label="Next day" data-testid="booking-next-day">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            {slotsData.slots.length === 0 ? (
              <p className="mt-6 text-muted-foreground text-sm" data-testid="booking-no-slots">
                No slots available on this day. Please try another date.
              </p>
            ) : (
              <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="slot-grid">
                {slotsData.slots.map((iso) => (
                  <button
                    key={iso}
                    onClick={() => bookSlot(iso)}
                    disabled={booking}
                    className="rounded-lg border border-input px-2 py-2.5 text-sm font-semibold hover:bg-primary hover:text-primary-foreground hover:border-primary transition disabled:opacity-50 tabular"
                    data-testid={`slot-${iso}`}
                  >
                    {fmtTime(iso)}
                  </button>
                ))}
              </div>
            )}

            {bookedAppt && (
              <div className="mt-6 rounded-xl border border-[hsl(var(--ld-jade))]/40 bg-accent p-5" data-testid="booking-success">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="text-[hsl(var(--ld-jade-fg))] mt-1" size={22} />
                  <div className="flex-1">
                    <p className="font-display font-semibold">You're booked for {fmtTime(bookedAppt.slot_datetime)}</p>
                    <p className="text-sm text-[hsl(var(--ld-jade-fg))] mt-1">
                      Show this OTP at the clinic to skip the queue:
                    </p>
                    <p className="mt-2 font-display text-3xl font-extrabold tabular tracking-widest text-[hsl(var(--ld-jade-fg))]" data-testid="booking-otp">
                      {bookedAppt.queue_otp}
                    </p>
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <Link to="/patient" className="btn-primary">View my visits</Link>
                      {wa && (
                        <a
                          href={`https://wa.me/${wa}?text=${encodeURIComponent(`Hi Dr. ${doctor.name}, I just booked a slot via LocalDoctor.in for ${fmtTime(bookedAppt.slot_datetime)}. My OTP is ${bookedAppt.queue_otp}.`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-secondary"
                        >
                          <MessageCircle size={16} /> Share on WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-border p-6">
            <h2 className="font-display text-xl font-semibold">Send a message</h2>
            <p className="text-muted-foreground text-sm">Can't book right now? Leave your number and the clinic will get in touch.</p>
            <form onSubmit={sendContact} className="mt-4 grid gap-3" data-testid="contact-form">
              <input className="input-base" placeholder="Your name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name" required />
              <input className="input-base tabular" placeholder="Phone number" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="contact-phone" required />
              <textarea className="input-base" rows={3} placeholder="How can the doctor help you? (optional)" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} data-testid="contact-message" />
              <button type="submit" disabled={sending} className="btn-primary" data-testid="contact-submit">
                {sending ? "Sending…" : "Send request"}
              </button>
            </form>
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-5">
          <div className="rounded-2xl border border-border p-5">
            <h3 className="font-display font-semibold">Clinic location</h3>
            <p className="text-sm text-foreground/85 mt-2">{doctor.address}</p>
            <div className="mt-3 aspect-video rounded-lg overflow-hidden border border-border">
              <iframe
                title="map"
                className="h-full w-full"
                src={`https://www.google.com/maps?q=${encodeURIComponent(doctor.clinic_name + ", " + doctor.address)}&output=embed`}
                loading="lazy"
              />
            </div>
          </div>
          <div className="rounded-2xl border border-border p-5 bg-secondary/40">
            <h3 className="font-display font-semibold">Verification status</h3>
            <p className="text-sm text-muted-foreground mt-2">
              {doctor.verified
                ? "This doctor's registration details have been reviewed by our team."
                : "This profile has not yet been verified by our team."}
            </p>
          </div>
          <div className="rounded-2xl border border-border p-5">
            <h3 className="font-display font-semibold">Medical disclaimer</h3>
            <p className="text-sm text-muted-foreground mt-2">
              LocalDoctor helps users discover doctors and clinics. Medical information and treatment
              decisions should always be discussed directly with a qualified medical professional.
            </p>
          </div>
        </aside>
      </section>
    </>
  );
}
